import React from "react";
import { format, startOfMonth } from "date-fns";
//Limitless imports
import { locale as translatableLocale } from "@limitless/common/utils";
import { DASHBOARD_METRICS, DASHBOARD_WEEKLY_METRICS, metrics, useGraphAPIHook } from "../../../portal";
import { useNotificationContext } from "@limitless/common/hooks";
import { useExpertProfileContext } from "../expert-profile/ExpertProfileContext";
import { getExpertStats } from "./ExpertStatsQueries";
import { fetchDashboardStats } from "../../apis/expert-stats-api/ExpertStatsAPI";
const DASHBOARD_OPTIONS = {
  numberOfDays: 7,
  dailyMetrics: DASHBOARD_METRICS,
  numberOfWeeks: 8,
  weeklyMetrics: DASHBOARD_WEEKLY_METRICS
};
const INITIAL_OPTIONS = {
  loading: true
};
export const useExpertStats = (shouldLoadDashboard = false, t) => {
  const [dashboardTrendMetrics, setDashboardTrendMetrics] = React.useState(INITIAL_OPTIONS);
  const [currentMonthState, setCurrentMonthMetricsState] = React.useState(INITIAL_OPTIONS);
  const [previousMonthState, setPreviousMonthMetricsState] = React.useState(INITIAL_OPTIONS);
  const [dashboard, setDashboard] = React.useState(INITIAL_OPTIONS);
  const {
    profile
  } = useExpertProfileContext();
  const {
    addFailureNotification
  } = useNotificationContext();
  const {
    fetchData
  } = useGraphAPIHook();
  const locale = React.useMemo(() => translatableLocale(t, true), []);
  React.useEffect(() => {
    if (profile.userName) {
      if (shouldLoadDashboard) {
        const today = new Date();
        const date = format(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7), "YYYY-MM-DD");
        getDashboardStats(date, date, {
          label: t("All Groups"),
          value: "all"
        }, [metrics[5].value], false, false, true);
      }
    }
  }, [profile]);
  const loadDashboardStats = lastWeekRating => {
    fetchDashboardStats(profile, DASHBOARD_OPTIONS, locale, result => {
      if (!result.hasError) {
        setDashboard({
          loading: false,
          lastWeekRating,
          ...result
        });
      } else {
        addFailureNotification("Failed to load dashboard stats, please refresh");
      }
    });
  };
  const getDashboardStats = (startDate, endDate, group = {
    label: t("All Groups"),
    value: "all"
  }, stats = [metrics[1].value], loadDashboardTrend = false, isCurrentMonth = false, lastWeekRating = false) => {
    const monthStartDate = isNaN(Date.parse(startDate)) ? format(startOfMonth(new Date()), "YYYY-MM-DD") : startDate;
    const monthEndDate = isNaN(Date.parse(endDate)) ? format(new Date(), "YYYY-MM-DD") : endDate;
    const variables = {
      groupByDate: "day",
      endDate: monthEndDate,
      startDate: monthStartDate,
      expertGroupName: group.value === "all" ? null : group.value,
      stats
    };
    const options = {
      dataKey: "getStats",
      query: getExpertStats(variables),
      variables,
      functionName: "ExpertStatsHook getStats",
      notifyDetails: {
        actionName: "Load dashboard stats",
        errorNotification: "Failed to load dashboard stats, please refresh"
      }
    };
    fetchData(options).then(result => {
      const range = getDatesInRange(startDate, endDate);
      let newSearchResults = {
        loading: false
      };
      if (lastWeekRating) {
        const lastWeekRating = result[0].metric[0].metricData[0].value;
        loadDashboardStats(lastWeekRating);
      }
      if (loadDashboardTrend) {
        newSearchResults = {
          ...newSearchResults,
          responded: FormateDate(range, findMetrics(result[0], stats[0])),
          escalate: FormateDate(range, findMetrics(result[0], stats[1])),
          peerReviewed: FormateDate(range, findMetrics(result[0], stats[2])),
          forwardReviewed: FormateDate(range, findMetrics(result[0], stats[3])),
          qualityReviewed: FormateDate(range, findMetrics(result[0], stats[4]))
        };
        return setDashboardTrendMetrics({
          ...newSearchResults
        });
      } else {
        if (stats.length === 1) {
          newSearchResults = {
            ...newSearchResults,
            result: FormateDate(range, findMetrics(result[0], stats[0]))
          };
        }
        if (stats.length === 5) {
          newSearchResults = {
            ...newSearchResults,
            responded: FormateDate(range, findMetrics(result[0], stats[0])),
            escalate: FormateDate(range, findMetrics(result[0], stats[1])),
            peerReviewed: FormateDate(range, findMetrics(result[0], stats[2])),
            forwardReviewed: FormateDate(range, findMetrics(result[0], stats[3])),
            qualityReviewed: FormateDate(range, findMetrics(result[0], stats[4]))
          };
        }
      }
      return isCurrentMonth ? setCurrentMonthMetricsState({
        ...newSearchResults
      }) : setPreviousMonthMetricsState({
        ...newSearchResults
      });
    }).catch(() => isCurrentMonth ? setCurrentMonthMetricsState({
      loading: false
    }) : setPreviousMonthMetricsState({
      loading: false
    }));
    return [currentMonthState, previousMonthState, dashboardTrendMetrics, getDashboardStats];
  };
  const findMetrics = (metrics, value) => {
    return metrics.metric.filter(stats => stats.name === value)[0];
  };
  function getDatesInRange(startDate, endDate) {
    const localStartDate = new Date(startDate.replaceAll("-", "/"));
    const localEndDate = new Date(endDate.replaceAll("-", "/"));
    const dates = [];
    while (localStartDate <= localEndDate) {
      const month = new Date(localStartDate).getMonth() + 1 < 10 ? `0${new Date(localStartDate).getMonth() + 1}` : new Date(localStartDate).getMonth() + 1;
      dates.push(`${new Date(localStartDate).getFullYear()}-${month}-${localStartDate.getDate() < 10 ? `0${localStartDate.getDate()}` : localStartDate.getDate()}`);
      localStartDate.setDate(localStartDate.getDate() + 1);
    }
    return dates;
  }
  const FormateDate = (dateRange, data) => {
    const row = [];
    dateRange.map(date => {
      if (!data) {
        row.push({
          date,
          value: 0
        });
        return;
      }
      const metricResult = data.metricData.find(data => data.date === date);
      if (metricResult) {
        row.push({
          date,
          value: +metricResult.value
        });
      } else {
        //if the platform doesn't return the date we requested, show a 0 value
        row.push({
          date,
          value: 0
        });
      }
    });
    return row;
  };
  const groupSuggestions = React.useMemo(() => {
    const groupList = [];
    profile.groups.map(group => {
      groupList.push({
        label: group.displayName,
        value: group.groupName
      });
    });
    groupList.sort((a, b) => {
      if (a.label.toLowerCase() < b.label.toLowerCase()) {
        return -1;
      }
      if (a.label.toLowerCase() > b.label.toLowerCase()) {
        return 1;
      }
      return 0;
    });
    groupList.unshift({
      label: t("All Groups"),
      value: "all"
    });
    return groupList;
  }, [profile]);
  return {
    dashboard,
    getDashboardStats,
    previousMonthState,
    currentMonthState,
    groupSuggestions,
    dashboardTrendMetrics
  };
};